export type HeartbeatSample = {
  latency: number;
  serverTime: number;
  ok: boolean;
};

export interface HeartbeatManagerConfig {
  intervalMs?: number;
  minLatency?: number;
  maxLatency?: number;
}

export class HeartbeatManager {
  private intervalHandle: ReturnType<typeof setInterval> | null = null;
  private readonly intervalMs: number;
  private readonly minLatency: number;
  private readonly maxLatency: number;
  private readonly onTick: (sample: HeartbeatSample) => void;

  constructor(
    onTick: (sample: HeartbeatSample) => void,
    config: HeartbeatManagerConfig = {},
  ) {
    this.onTick = onTick;
    this.intervalMs = config.intervalMs ?? 15000;
    this.minLatency = config.minLatency ?? 30;
    this.maxLatency = config.maxLatency ?? 350;
  }

  start(): void {
    if (this.intervalHandle) {
      return;
    }

    this.emitSample();
    this.intervalHandle = setInterval(() => {
      this.emitSample();
    }, this.intervalMs);
  }

  stop(): void {
    if (!this.intervalHandle) {
      return;
    }

    clearInterval(this.intervalHandle);
    this.intervalHandle = null;
  }

  ping(): HeartbeatSample {
    const sample = this.createSample();
    this.onTick(sample);
    return sample;
  }

  private emitSample(): void {
    this.onTick(this.createSample());
  }

  private createSample(): HeartbeatSample {
    const latency = Math.min(
      this.maxLatency,
      Math.max(this.minLatency, Math.round(this.minLatency + Math.random() * 220)),
    );

    return {
      latency,
      serverTime: Date.now(),
      ok: latency <= this.maxLatency,
    };
  }
}

export function createHeartbeatManager(
  onTick: (sample: HeartbeatSample) => void,
  config?: HeartbeatManagerConfig,
): HeartbeatManager {
  return new HeartbeatManager(onTick, config);
}
